import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { FavoriteComponent, AddFavoriteRequest } from '../types/customDashboard';
import { useAuth } from './AuthContext';
import { useBranch } from './BranchContext';
import { logger } from '../utils/logger';
import config from '../config/api.config';

interface FavoritesContextType {
  favorites: FavoriteComponent[];
  loading: boolean;
  error: string | null;
  addToFavorites: (request: AddFavoriteRequest) => Promise<void>;
  removeFromFavorites: (favoriteId: string) => Promise<void>;
  isFavorited: (componentId: string) => boolean;
  refreshFavorites: () => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<FavoriteComponent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { user, isLoading: authLoading } = useAuth();
  const { selectedBranch } = useBranch();
  
  // Load favorites from localStorage initially - user specific only
  useEffect(() => {
    logger.debug('FavoritesProvider useEffect', 'FavoritesContext', { 
      userId: user?.id, 
      authLoading,
      isAuthDisabled: config.STAGE_NO_AUTH
    });
    
    // Don't try to load if auth is still loading
    if (authLoading) {
      return;
    }
    
    // Only use default user if auth is explicitly disabled (true)
    const isAuthDisabled = config.STAGE_NO_AUTH;
    const userId = user?.id || (isAuthDisabled ? 'default-user' : null);
    if (userId) {
      loadFavorites();
    }
  }, [user?.id, authLoading]);
  
  const loadFavorites = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const isAuthDisabled = config.STAGE_NO_AUTH;
      const userId = user?.id || (isAuthDisabled ? 'default-user' : null);
      if (!userId) {
        setLoading(false);
        return;
      }
      
      // For now, using localStorage. Later will switch to API
      const storageKey = `favorites_${userId}`;
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const parsed = JSON.parse(stored);
        setFavorites(parsed);
      }
    } catch (err) {
      logger.error('Error loading favorites', 'FavoritesContext', err);
      setError('خطا در بارگذاری علاقه‌مندی‌ها');
    } finally {
      setLoading(false);
    }
  };
  
  const saveFavorites = (newFavorites: FavoriteComponent[]) => {
    const isAuthDisabled = config.STAGE_NO_AUTH;
    const userId = user?.id || (isAuthDisabled ? 'default-user' : null);
    if (!userId) {
      logger.warn('Cannot save favorites - no user ID available', 'FavoritesContext');
      return;
    }
    const storageKey = `favorites_${userId}`;
    localStorage.setItem(storageKey, JSON.stringify(newFavorites));
    setFavorites(newFavorites);
  };
  
  const addToCustomDashboard = (favorite: FavoriteComponent) => {
    try {
    const isAuthDisabled = config.STAGE_NO_AUTH;
      const userId = user?.id || (isAuthDisabled ? 'default-user' : null);
      if (!userId) return;
      
      const dashboardStorageKey = `tailwind_custom_dashboard_${userId}`;
      const storedDashboard = localStorage.getItem(dashboardStorageKey);
      
      let dashboardData = {
        items: [],
        updatedAt: new Date().toISOString()
      };
      
      if (storedDashboard) {
        dashboardData = JSON.parse(storedDashboard);
      }
      
      // Check if component is already in dashboard
      const existingItem = dashboardData.items.find(
        (item: any) => item.favoriteId === favorite.id
      );
      
      if (!existingItem) {
        const gridClass = favorite.gridLayout?.containerClass || 'grid grid-cols-1 gap-6';
        const newItem = {
          id: `item_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
          favoriteId: favorite.id,
          gridClass: gridClass
        };
        
        dashboardData.items.push(newItem);
        dashboardData.updatedAt = new Date().toISOString();
        
        localStorage.setItem(dashboardStorageKey, JSON.stringify(dashboardData));
      }
    } catch (error) {
      logger.error('Error adding to custom dashboard', 'FavoritesContext', error);
    }
  };
  
  const removeFromCustomDashboard = (favoriteId: string) => {
    try {
      const isAuthDisabled = config.STAGE_NO_AUTH;
      const userId = user?.id || (isAuthDisabled ? 'default-user' : null);
      if (!userId) return;
      
      const dashboardStorageKey = `tailwind_custom_dashboard_${userId}`;
      const storedDashboard = localStorage.getItem(dashboardStorageKey);
      
      if (storedDashboard) {
        const dashboardData = JSON.parse(storedDashboard);
        dashboardData.items = dashboardData.items.filter(
          (item: any) => item.favoriteId !== favoriteId
        );
        dashboardData.updatedAt = new Date().toISOString();
        
        localStorage.setItem(dashboardStorageKey, JSON.stringify(dashboardData));
      }
    } catch (error) {
      logger.error('Error removing from custom dashboard', 'FavoritesContext', error);
    }
  };
  
  const addToFavorites = async (request: AddFavoriteRequest) => {
    try {
      logger.debug('addToFavorites called', 'FavoritesContext', { 
        userId: user?.id, 
        userEmail: user?.email,
      isAuthDisabled: config.STAGE_NO_AUTH,
        hasUser: !!user 
      });
      
      if (!user?.id) {
        // Only use default user if auth is explicitly disabled
        const isAuthDisabled = config.STAGE_NO_AUTH;
        const defaultUserId = isAuthDisabled ? 'default-user' : null;
        if (!defaultUserId) {
          logger.warn('User not logged in - cannot add to favorites', 'FavoritesContext', {
            user,
            isAuthDisabled,
            token: localStorage.getItem('insight_auth_token') ? 'exists' : 'missing',
            userInfo: localStorage.getItem('insight_user_info') ? 'exists' : 'missing'
          });
          throw new Error('لطفاً ابتدا وارد سیستم شوید');
        }
        
        const newFavorite: FavoriteComponent = {
          id: `fav_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
          ...request,
          addedAt: new Date(),
          userId: defaultUserId
        };
        
        const updatedFavorites = [...favorites, newFavorite];
        saveFavorites(updatedFavorites);
        
        // Also add to custom dashboard automatically
        addToCustomDashboard(newFavorite);
        return;
      }
      
      const newFavorite: FavoriteComponent = {
        id: `fav_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        ...request,
        addedAt: new Date(),
        userId: user.id
      };
      
      const updatedFavorites = [...favorites, newFavorite];
      saveFavorites(updatedFavorites);
      
      // Also add to custom dashboard automatically
      addToCustomDashboard(newFavorite);
      
      // TODO: Call API to persist
    } catch (err) {
      logger.error('Error adding to favorites', 'FavoritesContext', err);
      throw err;
    }
  };
  
  const removeFromFavorites = async (favoriteId: string) => {
    try {
      const updatedFavorites = favorites.filter(f => f.id !== favoriteId);
      saveFavorites(updatedFavorites);
      
      // Also remove from custom dashboard
      removeFromCustomDashboard(favoriteId);
      
      // TODO: Call API to persist
    } catch (err) {
      logger.error('Error removing from favorites', 'FavoritesContext', err);
      throw err;
    }
  };
  
  const isFavorited = (componentId: string): boolean => {
    // Check both exact match and base component ID (without branch suffix)
    const baseComponentId = componentId.replace(/-\d+$/, ''); // Remove trailing -number (branchId)
    return favorites.some(f => 
      f.componentId === componentId || 
      f.componentId === baseComponentId ||
      componentId.startsWith(f.componentId + '-')
    );
  };
  
  const refreshFavorites = async () => {
    await loadFavorites();
  };
  
  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        loading,
        error,
        addToFavorites,
        removeFromFavorites,
        isFavorited,
        refreshFavorites
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
